import { createContext, useContext, useEffect, useState } from 'react'
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import type { UserProfile, Organization } from '../types'

interface AuthContextType {
  user: User | null
  session: Session | null
  profile: UserProfile | null
  organization: Organization | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, metadata?: any) => Promise<any>
  signOut: () => Promise<void>
  logout: () => Promise<void>
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>
  switchOrganization?: (orgId: string) => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)

  useEffect(() => {
    let mounted = true

    // 初期化処理
    const initializeAuth = async () => {
      try {
        // セッション取得
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (!mounted) return
        
        if (error) {
          console.error('Session error:', error)
          setLoading(false)
          return
        }

        if (session?.user) {
          setSession(session)
          setUser(session.user)
          // 初回のみプロファイルを読み込む
          if (!profile && !isLoadingProfile) {
            await loadUserProfile(session.user.id)
          }
        } else {
          setSession(null)
          setUser(null)
          setProfile(null)
          setOrganization(null)
        }
        
        if (mounted) {
          setLoading(false)
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        if (mounted) {
          setLoading(false)
        }
      }
    }

    initializeAuth()

    // 認証状態変更の監視
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, session: Session | null) => {
        console.log('Auth state change:', event)
        
        if (!mounted) return

        // サインイン・サインアウトイベントのみ処理
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'USER_UPDATED') {
          if (session?.user) {
            setSession(session)
            setUser(session.user)
            // プロファイルを読み込む（非同期で実行）
            if (!isLoadingProfile) {
              loadUserProfile(session.user.id)
            }
          } else {
            // ログアウト時の処理
            setSession(null)
            setUser(null)
            setProfile(null)
            setOrganization(null)
          }
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [isLoadingProfile, profile])

  const loadUserProfile = async (userId: string) => {
    // 既に読み込み中の場合はスキップ
    if (isLoadingProfile) {
      console.log('[loadUserProfile] Already loading profile, skipping...')
      return
    }
    
    setIsLoadingProfile(true)
    
    try {
      // プロファイル取得
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (profileError) {
        // プロファイルが存在しない場合（新規ユーザー）
        if (profileError.code === 'PGRST116' || (profileError as any).status === 406) {
          setProfile(null)
          setOrganization(null)
          return
        }
        
        // その他のエラーの場合はログアウト
        await supabase.auth.signOut()
        return
      }
      
      setProfile(profileData)

      // 組織情報取得
      if (profileData?.organization_id) {
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', profileData.organization_id)
          .single()

        if (orgError) {
          setOrganization(null)
        } else {
          setOrganization(orgData)
        }
      } else {
        setOrganization(null)
      }

    } catch (error) {
      console.error('Unexpected error in loadUserProfile:', error)
      // 予期しないエラーの場合はログアウト
      await supabase.auth.signOut()
    } finally {
      setIsLoadingProfile(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    console.log('Attempting sign in with:', email)
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      console.log('Sign in response:', { data, error })
      if (error) {
        console.error('Sign in error details:', {
          message: error.message,
          status: error.status,
          name: error.name
        })
        throw error
      }
      console.log('Sign in successful for:', data.user?.email)
    } catch (err) {
      console.error('Sign in caught error:', err)
      throw err
    }
  }

  const signUp = async (email: string, password: string, metadata?: any) => {
    console.log('Attempting sign up with:', email)
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata, // display_name, invitation_tokenなどを含む
        }
      })
      console.log('Sign up response:', { data, error })
      if (error) {
        console.error('Sign up error details:', {
          message: error.message,
          status: error.status,
          name: error.name
        })
        throw error
      }
      console.log('Sign up successful for:', data.user?.email)
      
      // 招待トークンがある場合、自動的に招待を受諾
      if (metadata?.invitation_token && data.user) {
        try {
          const { error: tokenError } = await supabase
            .rpc('use_invitation_token', {
              token: metadata.invitation_token,
              accepting_user_id: data.user.id
            })
          
          if (tokenError) {
            console.error('Failed to use invitation token:', tokenError)
          }
        } catch (err) {
          console.error('Error using invitation token:', err)
        }
      }
      
      return data
    } catch (err) {
      console.error('Sign up caught error:', err)
      throw err
    }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  }

  const logout = signOut // エイリアス

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('No user logged in')

    const { error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', user.id)

    if (error) throw error
    
    // ローカル状態更新
    setProfile(prev => prev ? { ...prev, ...updates } : null)
  }

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      organization,
      loading,
      signIn,
      signUp,
      signOut,
      logout,
      updateProfile,
      switchOrganization: undefined, // 今後実装予定
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}